import { z } from 'zod';
import type { IsoDate } from './common.js';
import type { UserSummary } from './users.js';

export const taskStatuses = ['todo', 'in_progress', 'done'] as const;
export const taskStatusSchema = z.enum(taskStatuses);
export type TaskStatus = z.infer<typeof taskStatusSchema>;

export const taskStatusLabels: Record<TaskStatus, string> = {
  todo: 'Pendiente',
  in_progress: 'En curso',
  done: 'Hecha',
};

/**
 * A column of the team's board (a custom state like «En revisión»). Each one belongs to a status
 * category, so «done» and «in progress» keep their meaning whatever the columns are called.
 */
export interface TaskColumn {
  id: string;
  name: string;
  category: TaskStatus;
  position: number;
}

/** Columns every team starts with. */
export const defaultTaskColumns: { name: string; category: TaskStatus }[] = [
  { name: 'Pendiente', category: 'todo' },
  { name: 'En curso', category: 'in_progress' },
  { name: 'Hecha', category: 'done' },
];

const columnNameSchema = z.string().trim().min(1, 'Obligatorio').max(40);

export const createTaskColumnSchema = z.object({
  name: columnNameSchema,
  category: taskStatusSchema,
});
export type CreateTaskColumnInput = z.infer<typeof createTaskColumnSchema>;

export const updateTaskColumnSchema = createTaskColumnSchema.partial();
export type UpdateTaskColumnInput = z.infer<typeof updateTaskColumnSchema>;

export const reorderTaskColumnsSchema = z.object({ ids: z.array(z.uuid()).min(1).max(30) });
export type ReorderTaskColumnsInput = z.infer<typeof reorderTaskColumnsSchema>;

export const deleteTaskColumnSchema = z.object({
  /** Column that receives the tasks of the deleted one. */
  moveTo: z.uuid(),
});
export type DeleteTaskColumnInput = z.infer<typeof deleteTaskColumnSchema>;

/**
 * The column a task shows in: its own, or (if it has none or it no longer exists) the first
 * column of its status category.
 */
export function columnOf<T extends Pick<TaskColumn, 'id' | 'category'>>(
  task: { columnId: string | null; status: TaskStatus },
  columns: T[],
): T | undefined {
  return (
    columns.find((c) => c.id === task.columnId) ?? columns.find((c) => c.category === task.status)
  );
}

export const taskPriorities = ['urgent', 'high', 'normal', 'low'] as const;
export const taskPrioritySchema = z.enum(taskPriorities);
export type TaskPriority = z.infer<typeof taskPrioritySchema>;

export const taskPriorityLabels: Record<TaskPriority, string> = {
  urgent: 'Urgente',
  high: 'Alta',
  normal: 'Normal',
  low: 'Baja',
};

export const taskTypes = ['task', 'story', 'bug', 'epic'] as const;
export const taskTypeSchema = z.enum(taskTypes);
export type TaskType = z.infer<typeof taskTypeSchema>;

export const taskTypeLabels: Record<TaskType, string> = {
  task: 'Tarea',
  story: 'Historia',
  bug: 'Error',
  epic: 'Épica',
};

/** Story points (estimation). */
export const storyPointsSchema = z.number().int().min(0).max(100);

/**
 * Short prefix of a team's task keys (`NEX` in `NEX-42`): 2–6 capital letters or digits, starting
 * with a letter.
 */
export const taskKeySchema = z
  .string()
  .trim()
  .toUpperCase()
  .regex(/^[A-Z][A-Z0-9]{1,5}$/, 'De 2 a 6 letras o números, empezando por una letra');

/** `NEX-42`. */
export const taskKeyOf = (teamKey: string, number: number) => `${teamKey}-${number}`;

/** A key prefix from a team name: initials of several words or the first letters of one. */
export function deriveTaskKey(name: string): string {
  const words = name
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .split(/[^A-Z0-9]+/)
    .filter((w) => /^[A-Z]/.test(w));
  const key =
    words.length > 1
      ? words
          .map((w) => w[0])
          .join('')
          .slice(0, 4)
      : (words[0] ?? '').slice(0, 3);
  return key.length >= 2 ? key : 'NEX';
}

/** Accepts `YYYY-MM-DD` (all-day due date). */
export const dueDateSchema = z.iso.date();

export const checklistItemSchema = z.object({
  id: z.string().min(1).max(40),
  text: z.string().trim().min(1).max(200),
  done: z.boolean().default(false),
});
export type ChecklistItem = z.output<typeof checklistItemSchema>;
export const checklistSchema = z.array(checklistItemSchema).max(50);

export const recurrenceFrequencies = ['daily', 'weekly', 'monthly'] as const;
export const recurrenceSchema = z.object({
  frequency: z.enum(recurrenceFrequencies),
  /** Every N days/weeks/months. */
  interval: z.number().int().min(1).max(12).default(1),
  /** Weekly only: days of the week (0 = Sunday … 6 = Saturday). */
  weekdays: z.array(z.number().int().min(0).max(6)).max(7).default([]),
});
export type Recurrence = z.output<typeof recurrenceSchema>;

/** Human text like «Cada semana (lun, jue)». */
export function recurrenceLabel(r: Recurrence): string {
  const days = ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'];
  const unit = { daily: ['día', 'días'], weekly: ['semana', 'semanas'], monthly: ['mes', 'meses'] }[
    r.frequency
  ];
  const base = r.interval === 1 ? `Cada ${unit[0]}` : `Cada ${r.interval} ${unit[1]}`;
  return r.frequency === 'weekly' && r.weekdays.length > 0
    ? `${base} (${[...r.weekdays]
        .sort()
        .map((d) => days[d])
        .join(', ')})`
    : base;
}

const toIsoDate = (d: Date) => d.toISOString().slice(0, 10);

/** Due date of the next occurrence after `dueDate` (YYYY-MM-DD, all-day, UTC). */
export function nextOccurrence(dueDate: string, r: Recurrence): string {
  const date = new Date(`${dueDate}T00:00:00Z`);
  if (r.frequency === 'daily') {
    date.setUTCDate(date.getUTCDate() + r.interval);
  } else if (r.frequency === 'monthly') {
    const day = date.getUTCDate();
    date.setUTCDate(1);
    date.setUTCMonth(date.getUTCMonth() + r.interval);
    const last = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 0)).getUTCDate();
    date.setUTCDate(Math.min(day, last));
  } else if (r.weekdays.length === 0) {
    date.setUTCDate(date.getUTCDate() + 7 * r.interval);
  } else {
    // Next listed weekday; after the last one of the week, jump `interval` weeks.
    const weekdays = [...new Set(r.weekdays)].sort();
    const current = date.getUTCDay();
    const later = weekdays.find((d) => d > current);
    if (later !== undefined) {
      date.setUTCDate(date.getUTCDate() + (later - current));
    } else {
      const first = weekdays[0] ?? current;
      date.setUTCDate(date.getUTCDate() + (7 - current + first) + 7 * (r.interval - 1));
    }
  }
  return toIsoDate(date);
}

export const createTaskSchema = z.object({
  title: z.string().trim().min(1, 'Obligatorio').max(200),
  description: z.string().trim().max(5000).optional(),
  status: taskStatusSchema.default('todo'),
  priority: taskPrioritySchema.default('normal'),
  dueDate: dueDateSchema.nullable().optional(),
  assigneeIds: z.array(z.uuid()).max(20).default([]),
  channelId: z.uuid().nullable().optional(),
  /** When converting a chat message into a task. */
  sourceMessageId: z.uuid().nullable().optional(),
  checklist: checklistSchema.default([]),
  recurrence: recurrenceSchema.nullable().optional(),
  type: taskTypeSchema.default('task'),
  storyPoints: storyPointsSchema.nullable().optional(),
  /** Sprint the task is planned in (none: the backlog). */
  sprintId: z.uuid().nullable().optional(),
  /** Board column; its category sets the status. */
  columnId: z.uuid().nullable().optional(),
});
export type CreateTaskInput = z.input<typeof createTaskSchema>;

export const updateTaskSchema = z
  .object({
    title: z.string().trim().min(1).max(200),
    description: z.string().trim().max(5000).nullable(),
    status: taskStatusSchema,
    priority: taskPrioritySchema,
    dueDate: dueDateSchema.nullable(),
    assigneeIds: z.array(z.uuid()).max(20),
    checklist: checklistSchema,
    recurrence: recurrenceSchema.nullable(),
    type: taskTypeSchema,
    storyPoints: storyPointsSchema.nullable(),
    sprintId: z.uuid().nullable(),
    columnId: z.uuid(),
  })
  .partial();
export type UpdateTaskInput = z.infer<typeof updateTaskSchema>;

export const listTasksQuerySchema = z.object({
  status: taskStatusSchema.optional(),
  /** `me` or a user id. */
  assignee: z.union([z.literal('me'), z.uuid()]).optional(),
  channelId: z.uuid().optional(),
});
export type ListTasksQuery = z.infer<typeof listTasksQuerySchema>;

export interface Task {
  id: string;
  teamId: string;
  channelId: string | null;
  sourceMessageId: string | null;
  title: string;
  description: string | null;
  status: TaskStatus;
  priority: TaskPriority;
  dueDate: string | null;
  createdBy: UserSummary;
  assignees: UserSummary[];
  createdAt: IsoDate;
  updatedAt: IsoDate;
  completedAt: IsoDate | null;
  checklist: ChecklistItem[];
  /** Completing a recurring task creates the next occurrence. */
  recurrence: Recurrence | null;
  type: TaskType;
  storyPoints: number | null;
  sprintId: string | null;
  /** Number within the team: with the team's `taskKey` it makes the key (`NEX-42`). */
  number: number;
  /** Board column (see TaskColumn); null for tasks from before custom columns. */
  columnId: string | null;
}
